import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { getPO, editPO } from "services/data.inbound.service";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSaveButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";
import { Success, Error_, InfoDialog, LoadingDialog } from "ui-components/Alert";
import BasicTable2 from "ui-components/tables/BasicTable2";

import PO from "./components/PO";
import PODetail from "./components/PODetails";
import { headerRender, headerValues, headerValidation } from "./po-schema";
import { detailRender, detailValues, detailValidation } from "./po-details-schema";
import { poColumns } from "./columns";
import {
  cancelDetail,
  deleteNewDetail,
  getTotalQuantity,
  skuValidation_,
  updateDetail,
} from "../common/common";
import { POCaption } from "../components/Caption";

import { dateValidation } from "layouts/views/common/validation";
import { poTypeLabel, statusLabel, statusObj } from "layouts/views/common/enum";

function EditPO2() {
  const location = useLocation();
  const navigate = useNavigate();
  const { poNumber, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const editPOMutation = useMutation(editPO, {});
  const { isFetching } = useQuery({
    queryKey: ["po", { poNumber, customerCode }],
    queryFn: () => getPO({ poNumber, customerCode }),
    onSuccess: (data) => {
      const { poDetails, poType, status, ...poValues } = data.data.data;
      setFormValues({
        ...poValues,
        status: statusLabel[status],
        poType: { label: poTypeLabel[poType], value: poType },
      });
      setDetails(poDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] })));
    },
    onError,
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;
  const headerProps = { headerRender, control, setValue, errors, mode: "EDIT" };

  const [vendorCode] = watch(["vendorCode"]);
  const detailProps = { details, setDetails, values: { customerCode, vendorCode } };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo(0, 0);
      return;
    }

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid Purchase Order Details" onChange={setAlert} />);
      return;
    }

    const { poType, ...data } = getValues();

    const isDateValid = dateValidation(details, data);
    if (isDateValid) {
      setAlert(<Error_ content="Invalid ETA Date must greater than PO Date" onChange={setAlert} />);
      return;
    }

    const req = {
      ...data,
      poType: poType.value,
      poDetails: details.map((d) => ({ ...d, status: statusObj[d.status], poUOM: d.poUOM.label })),
    };

    editPOMutation.mutate(
      { poNumber, customerCode, req },
      {
        onSuccess: (data) => navigate("/inbound/po", { state: data.data.message }),
        onError,
      }
    );
  };
  const editStatus = editPOMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {(isFetching || editStatus) && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <PO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSaveButton url="/inbound/po" onClick={onSubmit} disabled={editStatus} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...poColumns,
  ];
  const [alert, setAlert] = useState(null);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo, status: "Open", newItem: true },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode] = watch(["skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, skuCode },
    control,
    errors,
    setValue,
    getValues,
    onError,
    mode: "EDIT",
  };
  const onSubmit = (data) => {
    const isSKUDuplicate = skuValidation_(details, data);
    if (isSKUDuplicate) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }
    if (formValues === null) {
      setDetails([...details, data]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      setDetails(updateDetail(details, data));
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleDelete = (data) => {
    if (data.newItem === false) return;
    setDetails(deleteNewDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const handleCancel = (data) => {
    if (data.newItem === true) return;
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };
  const totalQuantity = getTotalQuantity(details, "qtyOrdered");

  const Caption = <POCaption totalRows={details.length} totalQuantity={totalQuantity} />;
  return (
    <InboundHeaderCard>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <PODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton
            disabled={formValues.status === "Cancel" || !isDirty}
            onClick={() => setFormValues(null)}
          />
        )}
      </form>

      <BasicTable2
        columns={columns}
        rows={details}
        caption={Caption}
        onRowClick={onRowClick}
        onDelete={handleDelete}
        onCancel={handleCancel}
        mode="EDIT"
      />
    </InboundHeaderCard>
  );
};

export default EditPO2;
